import { createReducer } from '@reduxjs/toolkit';

import { isOneOf } from '../../helpers/actions';
import { VerificationReducerState, VerificationStep } from '../interfaces';
import { isDebugHost } from '../../config/urls';

import { actionCreators } from './actions';

const defaultVerification: VerificationReducerState = {
  license_number: null,
  phone_number: null,
  verification_code: null,
  email: null,

  isLoading: false,

  error: null,

  step: VerificationStep.phoneNumber,

  authHeaders: {
    accessTokenLicense: null,
    accessTokenParams: null,
    accessTokenPhone: null,
    accountIdentifiers: null,
    phoneAuth: null,
    tcsConfig: null,
  },

  token_type: null,
  access_token: null,

  isLyftParamsLoading: false,
  isLyftParamsError: false,

  isLoginLoading: false,
  isLoginError: false,
};

const initialState: Readonly<VerificationReducerState> = {
  ...defaultVerification,
};

const verificationReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(actionCreators.getLyftHeaders.REQUEST, (state) => {
      state.isLyftParamsLoading = true;
      state.isLyftParamsError = false;

      state.token_type = null;
      state.access_token = null;
    })

    .addCase(actionCreators.getLyftHeaders.SUCCESS, (state, action) => {
      const {
        accessTokenLicense,
        accessTokenParams,
        accessTokenPhone,
        accountIdentifiers,
        phoneAuth,
        tcsConfig,
      } = action.payload;

      state.isLyftParamsError = false;
      state.authHeaders = {
        accessTokenLicense,
        accessTokenParams,
        accessTokenPhone,
        accountIdentifiers,
        phoneAuth,
        tcsConfig,
      };
    })

    .addCase(actionCreators.getLyftHeaders.FAILURE, (state) => {
      state.isLyftParamsLoading = false;
      state.isLyftParamsError = true;
    })

    .addCase(actionCreators.getLyftAuthAccessToken.REQUEST, (state) => {
      state.isLyftParamsLoading = true;
      state.isLyftParamsError = false;
    })

    .addCase(actionCreators.getLyftAuthAccessToken.SUCCESS, (state, action) => {
      const { token_type, access_token } = action.payload;

      state.isLyftParamsLoading = false;
      state.isLyftParamsError = false;
      state.token_type = token_type;
      state.access_token = access_token;
    })

    .addCase(actionCreators.getLyftAuthAccessToken.FAILURE, (state) => {
      state.isLyftParamsLoading = false;
      state.isLyftParamsError = true;
    })

    .addCase(actionCreators.verifyPhoneNumber.REQUEST, (state, action) => {
      const { value } = action.payload;

      state.error = null;
      state.phone_number = value;
      state.isLoading = true;
    })

    .addCase(actionCreators.verifyVerificationCode.REQUEST, (state, action) => {
      const { value } = action.payload;

      state.error = null;
      state.verification_code = value;
      state.isLoading = true;
    })

    .addCase(actionCreators.verifyLicenceNumber.REQUEST, (state, action) => {
      const { value } = action.payload;

      state.error = null;
      state.license_number = value;
      state.isLoading = true;
    })

    .addCase(actionCreators.verifyEmail.REQUEST, (state, action) => {
      const { value } = action.payload;

      state.email = value;
      state.isLoading = true;
      state.error = null;
    })

    .addCase(actionCreators.setStep, (state, action) => {
      const { step } = action.payload;

      state.step = step;
      state.error = null;
      state.isLoading = false;
    })

    .addCase(actionCreators.backToStep, (state) => {
      state.step = VerificationStep.phoneNumber;

      state.isLoading = false;
      state.verification_code = null;
      state.license_number = null;
      state.token_type = null;
      state.access_token = null;
      state.email = null;
      state.error = null;
      state.isLoginLoading = false;
      state.isLoginError = false;
    })

    .addCase(actionCreators.login.REQUEST, (state) => {
      state.isLoginLoading = true;
      state.isLoginError = false;
    })

    .addCase(actionCreators.login.SUCCESS, (state) => {
      Object.entries(defaultVerification).forEach(([key, value]) => {
        state[key] = value;
      });
    })

    .addCase(actionCreators.login.FAILURE, (state) => {
      state.isLoginLoading = false;
      state.isLoginError = true;
    })

    .addMatcher(
      isOneOf([
        actionCreators.verifyPhoneNumber.FAILURE,
        actionCreators.verifyVerificationCode.FAILURE,
        actionCreators.verifyLicenceNumber.FAILURE,
        actionCreators.verifyEmail.FAILURE,
      ]),
      (state, action) => {
        const { code, description, status } = action.payload;
        state.error = {
          code,
          description,
          status,
        };

        state.isLoading = false;
      }
    )
);

export default verificationReducer;
